import { useTranslation } from 'gatsby-plugin-react-i18next';
import { FullDate } from '../../hooks/useMemoizedFullDates';
import { vars } from '../../reactive';
import { Slot } from '../../reactive/main';
import translateService from '../../utils/translateService';
import { Slots } from '../../views/FormPages/Booking/DateAndTime';

export enum CHARGE_TYPE {
  PREPAID = 'PREPAID',
  POSTPAID = 'POSTPAID'
}

type Product = {
  id: string;
  name: string;
  price: number;
  charge_type: CHARGE_TYPE;
  quantity: number;
  total: number;
  original_price: number;
};

export const getMonth = (month: string, options: { includeYear?: boolean } = { includeYear: true }) => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(+month);
  date.setHours(0, 0, 0, 0);

  const monthName = date.toLocaleString('en-GB', {
    month: 'short',
    year: options.includeYear ? 'numeric' : undefined
  });

  return monthName;
};
export const getYear = (month: string) => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(+month);
  date.setHours(0, 0, 0, 0);

  return date.toLocaleString('en-GB', {
    year: 'numeric'
  });
};

export const getFormatedDate = (fullDate: Date | string) => {
  const { t, language } = translateService.get();
  const date = new Date(fullDate);
  const month = date.toLocaleString('en-GB', {
    month: 'long'
  });
  const day = date.toLocaleString('en-GB', {
    day: 'numeric'
  });

  const year = date.toLocaleString('en-GB', {
    year: 'numeric'
  });

  return `${formatOrdinals(+day, language)} ${t('of')} ${month}, ${year}`;
};

export const formatOrdinals = (n: number, locale: string = navigator.language) => {
  const pr = new Intl.PluralRules(locale, { type: 'ordinal' });
  const suffixesByLocale: Record<string, Map<string, string>> = {
    en: new Map([
      ['one', 'st'],
      ['two', 'nd'],
      ['few', 'rd'],
      ['other', 'th']
    ]),
    fr: new Map([
      ['one', 'er'],
      ['other', 'e']
    ]),
    ar: new Map([['other', '']]),
    es: new Map([
      ['one', 'er'],
      ['other', 'o']
    ])
  };

  const lang = locale.split('-')[0]; // e.g., 'en-US' → 'en'
  const suffixes = suffixesByLocale[lang] || suffixesByLocale['en'];

  const rule = pr.select(n);
  const suffix = suffixes.get(rule) ?? '';

  return `${n}${suffix}`;
};

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
export const THREE_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
export const MON_THREE_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

export const FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export const getBookingMonth = (date: Date | string) => {
  const fullDate = new Date(date);
  fullDate.setHours(0, 0, 0, 0);
  const fullDateMonth = fullDate.getMonth();
  const fullDateYear = fullDate.getFullYear();
  const currentYear = new Date().getFullYear();
  const bookingMonth = currentYear === fullDateYear ? fullDateMonth : fullDateMonth + 12;
  return bookingMonth;
};

export const findBookingDate = (bookings: Record<string, any>, fullDate: Date | string) => {
  const fd = new Date(fullDate);
  const dt = new Date(Date.UTC(fd.getUTCFullYear(), fd.getUTCMonth(), fd.getUTCDate(), fd.getUTCHours(), fd.getUTCMinutes(), 0, 0));
  const bookingMonth = getBookingMonth(dt);
  const booking = bookings[bookingMonth]?.find((bookingMonth: Record<string, any>) => compareDatesWithoutTime(bookingMonth.fullDate, dt));

  if (checkBookingAvailability(booking)) {
    return booking;
  }
};

export const getBookingDatesInBetween = (bookings: Record<string, any>, dates: (string | Date)[]) => {
  const startDate = dates[0];
  const endDate = dates[1];
  const startBookingMonth = getBookingMonth(startDate);
  const endBookingMonth = getBookingMonth(endDate);

  const bookingDates = Object.keys(bookings)
    .filter(bookingMonth => Number(bookingMonth) >= startBookingMonth && Number(bookingMonth) <= endBookingMonth)
    .map(bookingMonth => {
      return bookings[bookingMonth].filter((bookingMonth: Record<string, any>) => {
        const bookingDate = new Date(bookingMonth.fullDate);
        const bookingDateTimestamp = bookingDate.getTime();
        const startDateTimestamp = new Date(startDate).getTime();
        const endDateTimestamp = new Date(endDate).getTime();
        return bookingDateTimestamp >= startDateTimestamp && bookingDateTimestamp <= endDateTimestamp;
      });
    })
    .flat();

  return bookingDates;
};

export const getBookingDates = (dates: Date[], slots: Slots, options: { removeDaysWithoutSlots?: boolean }) => {
  const { removeDaysWithoutSlots } = options;
  const bookingDatesWithSlots =
    dates?.map((date, index) => {
      const fullDate = new Date(date);
      const fullDateDate = fullDate.getUTCDate();
      const day = THREE_DAYS[fullDate.getUTCDay()];
      const bookingSlots = slots?.availableSlots?.[index];
      const hasSlots = checkBookingAvailability({ slots: bookingSlots });

      if (removeDaysWithoutSlots && !hasSlots) {
        return;
      }

      return {
        ...date,
        fullDate,
        date: fullDateDate,
        day,
        slots: bookingSlots
      };
    }) || [];

  return bookingDatesWithSlots.filter(Boolean);
};

export const splitBookingDatesIntoMonths = (bookingDates: { fullDate: Date }[] = [], slice = 0) =>
  bookingDates.slice(slice).reduce((acc: { [key: string]: typeof bookingDates }, date) => {
    const bookingMonth = getBookingMonth(date.fullDate);
    const month = acc[bookingMonth];
    if (!month) {
      acc[bookingMonth] = [date];
    } else {
      acc[bookingMonth].push(date);
    }
    return acc;
  }, {});

export const getUTCTime = (slot: Slots['availableSlots'][0][0]) => {
  const date = new Date(slot?.timestamp);
  let hh = date.getUTCHours();
  let mm = date.getUTCMinutes();

  if (hh < 10) {
    hh = '0' + hh;
  }
  if (mm < 10) {
    mm = '0' + mm;
  }

  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12;
  hh = hh ? hh : 12;

  return `${hh}:${mm} ${ampm}`;
};

export const checkBookingAvailability = (booking: any) => {
  const isAvailable = booking?.slots?.some((slot: Slots['availableSlots'][0][0]) => slot.available > 0 || slot.isCheckoutOnly);
  return isAvailable;
};

export const checkIfSlotAvailable = (bookings: Record<string, any>, fullDate: Date) => {
  return checkBookingAvailability(findBookingDate(bookings, fullDate));
};

export const to12h = (hours: number = 0, minutes: number = 0, locale: string = 'en') => {
  const lang = locale.split('-')[0];

  // Only Arabic has different AM/PM labels
  const amPmLabels: Record<string, [string, string]> = {
    ar: ['ص', 'م'], // Arabic only
    default: ['AM', 'PM']
  };

  const hh = hours > 9 ? hours : `0${hours}`;
  const mm = minutes > 9 ? minutes : `0${minutes}`;
  const raw = `${hh}:${mm}`;

  const match = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (match) {
    const hour = Number(match[1]);
    const minute = match[2];
    const [am, pm] = amPmLabels[lang] || amPmLabels.default;
    const period = hour < 12 ? am : pm;
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${period}`;
  }

  return raw;
};

export const getTimestampDescription = (timestamp: FullDate, options: { noTime?: boolean } = { noTime: false }) => {
  const { t, language } = translateService.get();
  const { noTime } = options;

  const dateWithSuffix = formatOrdinals(Number(timestamp.date), language);
  const timestampDate = `${t(`daysFull.${timestamp.day}`)}, ${dateWithSuffix} ${t(`monthsShort.${timestamp.month}`)}`;

  if (noTime) {
    return timestampDate;
  }

  return `${timestampDate} - ${to12h(timestamp.hours, timestamp.minutes, language)}`;
};

export const getBiggestAndSmallestDates = (dates: (Date | string)[]) => {
  if (!Array.isArray(dates) || dates.length === 0) {
    return { biggestDate: '', smallestDate: '' };
  }

  if (dates.length === 1) {
    return { biggestDate: dates[0], smallestDate: dates[0] };
  }

  const biggestDate = dates.reduce((acc, date) => {
    const currentDate = new Date(date);
    const currentAccDate = new Date(acc);
    return currentDate > currentAccDate ? date : acc;
  }, dates[0]);

  const smallestDate = dates.reduce((acc, date) => {
    const currentDate = new Date(date);
    const currentAccDate = new Date(acc);
    return currentDate < currentAccDate ? date : acc;
  }, dates[0]);

  return { biggestDate, smallestDate };
};

export const isDateInBetween = (startDate: string | Date, endDate: string | Date, date: string | Date) => {
  if (!startDate || !endDate || !date) return false;
  const selectedDate = new Date(date);
  selectedDate.setHours(0, 0, 0, 0);
  const startTime = new Date(startDate);
  startTime.setHours(0, 0, 0, 0);
  const endTime = new Date(endDate);
  endTime.setHours(0, 0, 0, 0);

  return selectedDate.getTime() >= startTime.getTime() && selectedDate.getTime() <= endTime.getTime();
};

export const areDatesEqual = (date1: Date | string, date2: Date | string) => {
  if (!date1 || !date2) {
    return false;
  }
  const d1 = new Date(date1);
  d1.setHours(0, 0, 0, 0);
  const d2 = new Date(date2);
  d2.setHours(0, 0, 0, 0);
  return d1.getTime() === d2.getTime();
};

export const setCurrentDatePage = ({ newPage, animation, pageState }: { newPage: number; animation?: 'fade' | 'fade-out'; pageState: [number, React.Dispatch<React.SetStateAction<number>>] }) => {
  const [currentPage, setCurrentPage] = pageState;
  if (animation) {
    vars.formClass(animation);
  } else {
    newPage > currentPage ? vars.formClass('fade') : vars.formClass('fade-out');
  }

  setTimeout(() => {
    setCurrentPage(newPage);
  }, 50);
};

export const setSlotFieldBySession = ({
  orderGroupIndex,
  session,
  date,
  dateUntil,
  days,
  time,
  startDate,
  clearDate,
  repetition,
  BusUserId
}: {
  orderGroupIndex: number;
  session: number;
  date?: Slot['date'];
  days?: readonly ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  dateUntil?: Slot['dateUntil'];
  startDate?: Slot['startDate'];
  time?: Slot['time'];
  repetition?: Slot['repetition'];
  clearDate?: boolean;
  BusUserId?: string;
}) => {
  const newSelectedSlots = [...vars.selectedOrderGroupSlots()];
  const slots = newSelectedSlots[orderGroupIndex]?.slots;

  if (date && !dateUntil) {
    slots[session] = { date };
  } else {
    slots[session] = {
      ...slots[session],
      ...(date && { date }),
      ...(startDate && { startDate }),
      ...(dateUntil && { dateUntil }),
      ...(time && { time }),
      ...(days && { days }),
      ...(repetition && { repetition }),
      ...(BusUserId && { BusUserId })
    };
  }
  if (clearDate) {
    slots[session] = {};
  }
  newSelectedSlots[orderGroupIndex].slots = slots;
  vars.selectedOrderGroupSlots(newSelectedSlots);
};

export const clearLastSession = (sessions: number) => {
  setSlotFieldBySession({ clearDate: true, session: sessions - 1, orderGroupIndex: 0 });
};

export const compareDatesWithoutTime = (date1: Date | string, date2: Date | string) => {
  const d1 = toDateString(new Date(date1));
  const d2 = toDateString(new Date(date2));
  return d1 === d2;
};

export const isProductNoCharge = (product: Product) => {
  return product?.charge_type === CHARGE_TYPE.PREPAID && +product?.original_price === 0;
};

export const isProductPostpaid = (product: Product) => {
  return product?.charge_type === CHARGE_TYPE.POSTPAID;
};

export const isProductOtherSlotType = () => {
  const selectedProduct = vars.selectedProduct();
  return selectedProduct?.booking_type === 'SLOT' && selectedProduct.slots.length === 1;
};

export const sideScroll = (element: HTMLDivElement, direction: 'left' | 'right') => {
  const step = direction === 'left' ? -10 : 10;
  const distance = 450;
  let scrollAmount = 0;
  const slideTimer = setInterval(() => {
    element.scrollLeft += step;
    scrollAmount += Math.abs(step);
    if (scrollAmount >= distance) {
      clearInterval(slideTimer);
    }
  }, 2);
};

export const checkIfSlotsCompleted = (selectedSlots: Slot[], product: Queries.CollarAPI_Product) => {
  const multiDayProduct = product?.booking_type === 'MULTI_DAY';
  const recurrenceType = vars.recurrenceType();
  const isRecurring = recurrenceType === 'recurring' && product.allows_repeated_orders;
  return (
    selectedSlots.every(slot => {
      if (isRecurring) {
        return slot.days?.length && slot.startDate;
      }

      if (multiDayProduct) {
        return slot.date && slot.dateUntil;
      }

      return slot.date && slot.time;
    }) && selectedSlots.length === product?.sessions
  );
};

export function toReadableDate(
  date: Date | string,
  options: {
    noTime?: boolean;
    withDay?: boolean;
  } = { noTime: false, withDay: false }
): string {
  const { noTime, withDay } = options;
  const d = new Date(date);
  if (!d.getTime()) return '';
  const { t } = useTranslation();
  const day = d.getUTCDate();
  const monthIndex = d.getUTCMonth();
  const year = d.getUTCFullYear();
  const weekdayIndex = d.getUTCDay();
  const hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();

  const translatedMonth = t(`monthsShort.${MONTH_NAMES[monthIndex]}`);
  const translatedWeekday = t(`daysShort.${THREE_DAYS[weekdayIndex]}`);

  const dateString = withDay ? `${translatedWeekday}, ${day} ${translatedMonth} ${year}` : `${day} ${translatedMonth} ${year}`;

  if (noTime) return dateString;

  const hh = hours < 10 ? `0${hours}` : hours;
  const mm = minutes < 10 ? `0${minutes}` : minutes;

  return `${dateString} - ${hh}:${mm}`;
}

export const toDateString = (d: Date) => {
  const date = new Date(d || Date.now());
  const stringUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), 0, 0)).toDateString();
  return stringUTC;
};
